import { useLayoutEffect, useState, useRef } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import type { ComponentPropsWithoutRef } from 'react';
import { isUndefined } from 'lodash';

import { InternalSelectContext } from './context';
import { adapterMap } from './adapter';
import type { SelectContextType } from './context';
import type { SelectValueAdapter } from './adapter';
import type { DataType, ValueType } from './types';

type OriginalSelectProps = Omit<
  ComponentPropsWithoutRef<typeof SelectPrimitive.Root>,
  'onValueChange' | 'value' | 'defaultValue'
>;

type OnValueChange<Value> = (value: Value) => void;

type SelectProps<_Variant extends DataType> = {
  value?: _Variant extends 'string' ? string : number;
  defaultValue?: _Variant extends 'string' ? string : number;
  onValueChange?: OnValueChange<_Variant extends 'string' ? string : number>;
  variant?: _Variant;
} & OriginalSelectProps;

const Select = <_Variant extends DataType = 'string'>({
  value,
  defaultValue,
  onValueChange,
  variant = 'string' as _Variant,
  ...props
}: SelectProps<_Variant>) => {
  const [side, setSide] = useState<SelectContextType['side']>('bottom');
  const contentRef = useRef<React.ElementRef<typeof SelectPrimitive.Content> | null>(null);

  // The Radix Select only supports strings as values, we're using the adapter to serialize and
  // deserialize the value to support both strings and numbers.
  const adapter = adapterMap[variant] as SelectValueAdapter<ValueType>;

  const handleValueChange = (_value: string) => {
    (onValueChange as OnValueChange<ValueType>)?.(adapter.deserialize(_value));
  };

  // Radix doesn't expose the value of the side where the content is positioned relative
  // to the trigger, this is a workaround to get the side directly from the content element
  // biome-ignore lint/correctness/useExhaustiveDependencies: The effect needs to be memoized
  useLayoutEffect(() => {
    queueMicrotask(() => {
      if (contentRef.current) {
        const dataSide = contentRef.current.getAttribute('data-side') as SelectContextType['side'];
        if (dataSide) {
          setSide(dataSide);
        }
      }
    });
  }, [contentRef.current]);

  return (
    <InternalSelectContext.Provider
      value={{
        side,
        setSide,
        contentRef,
        adapter,
      }}
    >
      <SelectPrimitive.Root
        defaultValue={isUndefined(defaultValue) ? defaultValue : adapter.serialize(defaultValue)}
        value={isUndefined(value) ? value : adapter.serialize(value)}
        onValueChange={handleValueChange}
        {...props}
      />
    </InternalSelectContext.Provider>
  );
};
Select.displayName = SelectPrimitive.Root.displayName;

export { Select };
